<script setup>
import { computed, ref } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import LinkPreview from './LinkPreview.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { firstWebLink } from 'shared/helpers/MessageFormatter';
import {
  ATTACHMENT_TYPES,
  MESSAGE_TYPES,
  MESSAGE_VARIANTS,
} from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  inReplyTo,
  variant,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);
const isMobileViewport = useMediaQuery('(max-width: 767px)');

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

// Aceleres 2026-08-11: mensagem de texto longa trunca em blocos de 770
// caracteres com "..." e um "Ler mais" — cada clique revela mais 770 (nao
// o texto inteiro de uma vez), e o botao reaparece se ainda sobrar mais
// depois disso, ate mostrar tudo. `visibleLength` e a marca d'agua de
// quanto ja foi revelado; comeca em 770 (1 bloco) e cresce 770 por clique.
const CONTENT_TRUNCATE_LENGTH = 770;
const visibleLength = ref(CONTENT_TRUNCATE_LENGTH);
const hasMoreContent = computed(
  () => (renderContent.value || '').length > visibleLength.value
);
const visibleContent = computed(() => {
  const text = renderContent.value || '';
  if (text.length <= visibleLength.value) return text;

  return `${text.slice(0, visibleLength.value)}...`;
});
const showMoreContent = () => {
  visibleLength.value += CONTENT_TRUNCATE_LENGTH;
};

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const hasMediaAttachments = computed(() =>
  attachments.value?.some(attachment =>
    [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO].includes(
      attachment.fileType
    )
  )
);

// Aceleres: badge "editada" (estilo WhatsApp). Fica no fluxo do texto, não
// no MessageMeta absoluto do BaseBubble — assim não vaza quando a mensagem
// é curta ("hola"). content_attributes.edited é setado pelo patch_content
// do backend em app/controllers/api/v1/accounts/conversations/messages_controller.rb.
// `edited` e o carimbo antigo; `edited_at` e o que TODOS os caminhos de edicao
// gravam hoje (patch_content do botao Editar, a ponte WAME e o
// /internal/update-message-content, quando o contato edita no WhatsApp). Ler os
// dois evita que uma edicao vinda de um caminho que so grava a data passe sem
// marcador. O `camelCase` esta aqui porque parte do painel recebe
// `content_attributes` ja camelizado.
const editedAt = computed(() => {
  const attrs = contentAttributes.value || {};
  return attrs.editedAt || attrs.edited_at || null;
});
const isEdited = computed(
  () => !!contentAttributes.value?.edited || !!editedAt.value
);
const editedTitle = computed(() => {
  if (!editedAt.value) return undefined;
  const date = new Date(editedAt.value);
  return Number.isNaN(date.getTime())
    ? undefined
    : `Editada em ${date.toLocaleString()}`;
});

// Aceleres -- primeiro endereco do texto: e o que ganha o cartao de previa,
// como no WhatsApp (a mensagem com varios links so mostra o primeiro).
const previewUrl = computed(() => {
  const contentText = String(renderContent.value || '');
  return isMobileViewport.value
    ? firstWebLink(contentText)
    : contentText.match(/https?:\/\/[^\s<>"']+/)?.[0] || '';
});

// Aceleres 2026-08-19: mensagem que e SO um emoji passa a ser desenhada como
// figurinha — sem o fundo da bolha, com a barra do nome em cima e o horario
// numa pilha propria embaixo. Quem responde "e so um emoji?" e o parse do
// twemoji (evento `single-emoji` do FormattedContent): a conta ja vivia la,
// para o emoji grande, e reimplementa-la aqui com uma expressao regular
// criaria uma segunda regra a divergir da primeira.
//
// Por vir do parse, a resposta chega DEPOIS do primeiro render — o mesmo
// instante em que o emoji ja mudava de tamanho hoje, entao nao ha um salto
// novo no desenho.
const isSingleEmoji = ref(false);

// A bolha so troca de desenho quando nao ha mais nada nela: citacao, previa de
// link, badge de "editada", traducao ou anexo continuam a precisar do fundo
// para se lerem. Sem estes guardas, responder a alguem com um emoji deixava o
// bloco citado a flutuar sobre o papel de parede.
const isEmojiBubble = computed(
  () =>
    isSingleEmoji.value &&
    variant.value !== MESSAGE_VARIANTS.PRIVATE &&
    !inReplyTo?.value &&
    !previewUrl.value &&
    !isEdited.value &&
    !hasTranslations.value &&
    !attachments.value?.length
);

// Aceleres 2026-08-19: o horario partilha a ULTIMA LINHA do texto, como no
// WhatsApp — um espacador invisivel no fim do conteudo abre o lugar dele, e a
// linha quebra sozinha quando nao cabe. Antes o lugar era uma faixa por baixo
// do texto, e isso deixava toda mensagem ~20px mais alta do que precisava.
//
// So quando o texto e a ULTIMA coisa da bolha: com "editada", tradutor ou
// chips de anexo, o fim do conteudo nao e o fim da ultima linha e o espacador
// nao reservaria nada — nesses casos a faixa continua (Base.vue,
// `metaReservesBand`). A previa de link vem ANTES do texto, portanto o endereco
// continua a ser a ultima linha e pode partilhar o horario sem criar uma faixa
// vazia por baixo da mensagem.
const usesInlineMetaSpacer = computed(
  () =>
    !isEmojiBubble.value &&
    !(isSingleEmoji.value && variant.value === MESSAGE_VARIANTS.PRIVATE) &&
    !!renderContent.value &&
    !isEdited.value &&
    !hasTranslations.value &&
    !attachments.value?.length &&
    !isTemplate.value
);

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    class="w-fit max-w-full break-words"
    :class="[
      isEmojiBubble ? 'acl-emoji-bubble' : 'acl-text-bubble px-2.5 py-1.5',
      { 'acl-text-bubble--with-media': hasMediaAttachments },
    ]"
    :text-sender-name="!isEmojiBubble"
    :show-sender-name="isEmojiBubble"
    :media-caption="isEmojiBubble"
    :inline-meta-spacer="usesInlineMetaSpacer"
    :data-bubble-name="isEmojiBubble ? 'emoji' : 'text'"
  >
    <div
      class="acl-message-content flex flex-col"
      :class="previewUrl ? 'gap-1' : 'gap-2'"
    >
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <LinkPreview v-if="previewUrl" :url="previewUrl" />
      <span v-if="renderContent" class="[&_p]:!inline [&_p]:!m-0">
        <FormattedContent
          :content="visibleContent"
          :large-single-emoji="!attachments?.length"
          class="acl-message-caption"
          @single-emoji="isSingleEmoji = $event"
        />
        <button
          v-if="hasMoreContent"
          type="button"
          class="inline font-bold text-[#1b8755] no-underline -ml-px !m-0 !p-0 border-0 leading-none"
          @click.stop="showMoreContent"
        >
          Ler mais
        </button>
        <!-- O lugar do horario na propria linha. Largura em
             aceleres-overrides.css (procure por "acl-meta-spacer"), porque
             ela depende do recheio da bolha, que tambem vive la. -->
        <span
          v-if="usesInlineMetaSpacer"
          class="acl-meta-spacer"
          aria-hidden="true"
        />
      </span>
      <span
        v-if="isEdited"
        class="text-[11px] opacity-70 leading-tight -mt-1"
        :title="editedTitle"
        >editada</span
      >
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
